import { Fragment } from 'react';
import { Link, useParams } from 'react-router-dom';
import Decimal from 'decimal.js';
import { Badge, Table } from 'react-bootstrap';
import {
  useGetCashSessionQuery,
  useListCashSessionTransactionsQuery,
  useLazyGetCashSessionReportPdfQuery,
} from '../../../redux/api/marktApi';
import FormatDateTime from '../../../components/dateTime/FormatDateTime';
import PdfButton from '../../../components/pdf/PdfButton';

const euro = (v) => `${new Decimal(v || 0).toFixed(2)} €`;

const txLabel = (tx) => {
  if (tx.transfer) return tx.type === 'IN' ? 'Einzahlung' : 'Entnahme';
  return tx.type === 'IN' ? 'Verkauf' : 'Auszahlung';
};

const SessionDetail = () => {
  const { sessionId } = useParams();
  const { data, isLoading } = useGetCashSessionQuery(sessionId);
  const { data: txs, isLoading: txLoading } =
    useListCashSessionTransactionsQuery(sessionId);
  const [triggerSessionPdf] = useLazyGetCashSessionReportPdfQuery();

  if (isLoading || !data) return <p>Sitzung wird geladen...</p>;

  const { session, summary } = data;
  const isRunning = session.status === 'RUNNING';

  return (
    <Fragment>
      <div className='d-flex justify-content-between align-items-center mt-2'>
        <h4 className='mb-0'>Kassensitzung – {session.cash_register_name}</h4>
        <div className='d-flex gap-2'>
          <PdfButton
            onFetch={() => triggerSessionPdf(sessionId).unwrap()}
            filename={`kassenschluss-${sessionId.slice(0, 8)}.pdf`}
            label='Kassenschlussbericht (PDF)'
            variant='outline-dark'
          />
          <Link
            to='/markt/cash-register/overview'
            className='btn btn-outline-secondary btn-sm'
          >
            <i className='fas fa-arrow-left me-1' />
            zur Übersicht
          </Link>
        </div>
      </div>
      <p className='text-muted mt-2'>
        {isRunning ? (
          <Badge bg='success'>läuft</Badge>
        ) : (
          <Badge bg='secondary'>geschlossen</Badge>
        )}{' '}
        geöffnet von {session.opened_by} am{' '}
        <FormatDateTime dateTime={session.opened_at} />
        {session.closed_at && (
          <>
            {' '}
            · geschlossen von {session.closed_by} am{' '}
            <FormatDateTime dateTime={session.closed_at} />
          </>
        )}
      </p>

      <div className='row g-3 my-2'>
        <div className='col-md-6'>
          <div className='card h-100'>
            <div className='card-header'>Übersicht</div>
            <div className='card-body'>
              <Table size='sm' borderless className='mb-0'>
                <tbody>
                  <tr>
                    <td>Anfangsbestand</td>
                    <td className='text-end'>{euro(summary.start_balance)}</td>
                  </tr>
                  <tr>
                    <td>Tageseinnahmen</td>
                    <td className='text-end'>{euro(summary.revenue)}</td>
                  </tr>
                  <tr>
                    <td>Einzahlungen</td>
                    <td className='text-end'>{euro(summary.transfers_in)}</td>
                  </tr>
                  <tr>
                    <td>Entnahmen</td>
                    <td className='text-end'>
                      − {euro(summary.transfers_out)}
                    </td>
                  </tr>
                  <tr className='fw-bold border-top'>
                    <td>
                      {isRunning
                        ? 'Erwarteter Kassenstand'
                        : 'Endbestand (Wechselgeld)'}
                    </td>
                    <td className='text-end'>
                      {euro(
                        isRunning
                          ? summary.expected_balance
                          : session.end_balance
                      )}
                    </td>
                  </tr>
                  {!isRunning && (
                    <Fragment>
                      <tr>
                        <td>Gezählt</td>
                        <td className='text-end'>
                          {euro(session.sum_counting)}
                        </td>
                      </tr>
                      <tr>
                        <td>Entnahme Kassenschluss</td>
                        <td className='text-end'>
                          {euro(session.final_transfer_out)}
                        </td>
                      </tr>
                    </Fragment>
                  )}
                </tbody>
              </Table>
            </div>
            <div className='card-footer'>
              {summary.reconciled ? (
                <span className='text-success'>
                  <i className='fas fa-check-circle me-1' />
                  Kassenbuch stimmt mit dem Kassenstand überein
                </span>
              ) : (
                <span className='text-danger'>
                  <i className='fas fa-triangle-exclamation me-1' />
                  Differenz zum Kassenbuch (Ledger:{' '}
                  {euro(summary.ledger_balance)})!
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <h5 className='mt-3'>Kassenbewegungen</h5>
      {txLoading ? (
        <p>Bewegungen werden geladen...</p>
      ) : (
        <Table striped hover size='sm' responsive>
          <thead>
            <tr>
              <th>Zeit</th>
              <th>Art</th>
              <th className='text-end'>Betrag</th>
              <th>Kommentar</th>
              <th>Benutzer</th>
            </tr>
          </thead>
          <tbody>
            {(txs || []).map((tx) => (
              <tr key={tx.id}>
                <td>
                  <FormatDateTime dateTime={tx.created_at} />
                </td>
                <td>{txLabel(tx)}</td>
                <td className='text-end'>
                  {tx.type === 'OUT' ? '− ' : ''}
                  {euro(tx.amount)}
                </td>
                <td>{tx.comment}</td>
                <td>{tx.created_by}</td>
              </tr>
            ))}
            {(!txs || txs.length === 0) && (
              <tr>
                <td colSpan={5} className='text-center text-muted'>
                  Keine Bewegungen.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
    </Fragment>
  );
};

export default SessionDetail;
